import React from "react";
import { Row, Col, FormGroup, Label, Input, FormFeedback, Badge } from "reactstrap";
import { Company, CompanyFormValues } from "./types";
import { FormikProps } from "formik";

interface CompanyFormProps {
    company: Company | null;
    isEditOrCreate: boolean;
    formValidation: FormikProps<CompanyFormValues>;
}

const CompanyForm: React.FC<CompanyFormProps> = ({ 
    company, 
    isEditOrCreate, 
    formValidation 
}) => {
    return (
        <Row>
            <Col lg={6}>
                <FormGroup row className="align-items-center">
                    <Label for="tradeName" sm={4} className="fs-14 fw-bold mb-0">Nombre Comercial:</Label>
                    <Col sm={8}>
                        {isEditOrCreate ? (
                            <Input
                                id="tradeName"
                                name="tradeName"
                                placeholder="Nombre Comercial"
                                value={formValidation.values.tradeName}
                                onChange={formValidation.handleChange}
                                invalid={!!formValidation.touched.tradeName && !!formValidation.errors.tradeName}
                            />
                        ) : (
                            <p className="fs-14 text-muted mb-0">
                                {company?.tradeName || "N/A"}
                            </p>
                        )}
                        {formValidation.touched.tradeName && formValidation.errors.tradeName ? (
                            <FormFeedback className="d-block">
                                {String(formValidation.errors.tradeName)}
                            </FormFeedback>
                        ) : null}
                    </Col>
                </FormGroup>

                <FormGroup row>
                    <Label for="ruc" sm={4} className="fw-bold">RUC:</Label>
                    <Col sm={8}>
                        {isEditOrCreate ? 
                            <Input 
                                id="ruc" 
                                name="ruc" 
                                value={formValidation.values.ruc} 
                                onChange={formValidation.handleChange} 
                                invalid={!!formValidation.touched.ruc && !!formValidation.errors.ruc} 
                            /> : 
                            <p className="text-muted mb-0">{company?.ruc}</p>
                        }
                    </Col>
                </FormGroup>

                <FormGroup row>
                    <Label for="address" sm={4} className="fw-bold">Dirección:</Label>
                    <Col sm={8}>
                        {isEditOrCreate ? 
                            <Input 
                                id="address" 
                                name="address" 
                                value={formValidation.values.address} 
                                onChange={formValidation.handleChange} 
                                invalid={!!formValidation.touched.address && !!formValidation.errors.address} 
                            /> : 
                            <p className="text-muted mb-0">{company?.address}</p>
                        }
                    </Col>
                </FormGroup>

                <FormGroup row>
                    <Label for="agentRetentionResolution" sm={4} className="fw-bold">Agente de Retención:</Label>
                    <Col sm={8}>
                        {isEditOrCreate ? 
                            <Input 
                                id="agentRetentionResolution" 
                                name="agentRetentionResolution" 
                                value={formValidation.values.agentRetentionResolution} 
                                onChange={formValidation.handleChange} 
                            /> : 
                            <p className="text-muted mb-0">{company?.agentRetentionResolution || "N/A"}</p>
                        }
                    </Col>
                </FormGroup>
            </Col>

            <Col lg={6}>
                <FormGroup row className="align-items-center">
                    <Label for="businessName" sm={4} className="fs-14 fw-bold mb-0">Razón Social:</Label>
                    <Col sm={8}>
                        {isEditOrCreate ? (
                            <>
                                <Input
                                    id="businessName"
                                    name="businessName"
                                    placeholder="Razón Social"
                                    value={formValidation.values.businessName}
                                    onChange={formValidation.handleChange}
                                    invalid={!!formValidation.touched.businessName && !!formValidation.errors.businessName}
                                />
                                {formValidation.touched.businessName && formValidation.errors.businessName && (
                                    <FormFeedback className="d-block">
                                        {String(formValidation.errors.businessName)}
                                    </FormFeedback>
                                )}
                            </>
                        ) : (
                            <p className="fs-14 text-muted mb-0">
                                {company?.businessName || "Sin Razón Social"}
                            </p>
                        )}
                    </Col>
                </FormGroup>

                <FormGroup row>
                    <Label for="rimpeType" sm={4} className="fw-bold">Régimen:</Label>
                    <Col sm={8}>
                        {isEditOrCreate ? (
                            <Input 
                                type="select" 
                                id="rimpeType" 
                                name="rimpeType" 
                                value={formValidation.values.rimpeType} 
                                onChange={formValidation.handleChange}
                            >
                                <option value="REGIMEN_GENERAL">Régimen General</option>
                                <option value="NEGOCIO_POPULAR">Negocio Popular</option>
                                <option value="EMPRENDEDOR">Emprendedor</option>
                            </Input>
                        ) : 
                            <Badge color="soft-info" className="text-info text-uppercase">
                                {company?.rimpeType ? company?.rimpeType.replace('_', ' ') : "RÉGIMEN_GENERAL"}
                            </Badge>
                        }
                    </Col>
                </FormGroup>

                <FormGroup row>
                    <Label for="obligedAccounting" sm={4} className="fw-bold">¿Obligado a llevar contabilidad?</Label>
                    <Col sm={8}>
                        {isEditOrCreate ? (
                            <div className="form-check form-switch">
                                <Input 
                                    className="form-check-input" 
                                    type="checkbox" 
                                    role="switch" 
                                    id="obligedAccounting" 
                                    name="obligedAccounting" 
                                    checked={formValidation.values.obligedAccounting} 
                                    onChange={formValidation.handleChange} 
                                />
                            </div>
                        ) : (
                            company?.obligedAccounting ? 
                                <span className="text-success">
                                    <i className="ri-check-line align-bottom"></i> Sí
                                </span> : 
                                <span className="text-danger">
                                    <i className="ri-close-line align-bottom"></i> No
                                </span>
                        )}
                    </Col>
                </FormGroup>

                <FormGroup row className="align-items-center">
                    <Label for="logoUrl" sm={4} className="fs-14 fw-bold mb-0">URL Logo:</Label>
                    <Col sm={8}>
                        {isEditOrCreate ? (
                            <>
                                <Input
                                    id="logoUrl"
                                    name="logoUrl"
                                    value={formValidation.values.logoUrl}
                                    onChange={formValidation.handleChange}
                                    placeholder="https://su-sitio.com/logo.png"
                                    invalid={!!formValidation.touched.logoUrl && !!formValidation.errors.logoUrl}
                                />
                                {formValidation.touched.logoUrl && formValidation.errors.logoUrl && (
                                    <FormFeedback className="d-block">
                                        {String(formValidation.errors.logoUrl)}
                                    </FormFeedback>
                                )}
                            </>
                        ) : (
                            <p className="fs-14 mb-0">
                                {company?.logoUrl ? (
                                    <a href={company.logoUrl} target="_blank" rel="noreferrer" className="text-primary text-decoration-underline">
                                        <i className="ri-external-link-line me-1"></i> Ver logo actual
                                    </a>
                                ) : (
                                    <span className="text-muted">Sin logo configurado</span>
                                )}
                            </p>
                        )}
                    </Col>
                </FormGroup>
            </Col>
        </Row>
    );
};

export default CompanyForm;
